import { parseDate, formatDate, computeAge } from './dateUtils';

export const formatFieldValue = (value) => {
  if (value === null || value === undefined) {
    return '—';
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length ? trimmed : '—';
  }

  return String(value);
};

export const computeAgeFromDate = (dateString) => computeAge(dateString);

export const formatDateValue = (value) => {
  if (!value) return '—';
  const display = formatDate(value);
  return display === '—' ? formatFieldValue(value) : display;
};

export const getPatientId = (patient) =>
  patient?.__internalId ??
  patient?.id ??
  patient?.unique_id ??
  patient?.UNIQUE_ID ??
  patient?.['UNIQUE ID'] ??
  patient?.['ΑΡΙΘΜΟΣ ΚΑΡΤΕΛΑΣ'] ??
  patient?.['LOGIN'] ??
  null;

export const PATIENT_DETAIL_FIELD_CONFIG = [
  { key: 'last_name', label: 'Last Name' },
  { key: 'first_name', label: 'First Name' },
  { key: '__amka', label: 'AMKA' },
  { key: '__age', label: 'Age' },
  { key: '__dateOfBirth', label: 'Date of Birth' },
  { key: '__gender', label: 'Gender' },
  { key: '__cardDate', label: 'Card Date' },
  { key: '__phone', label: 'Phone' },
  { key: '__diagnosis', label: 'Diagnosis', multiline: true },
  { key: '__am', label: 'AM' },
  { key: '__address', label: 'Address', multiline: true },
  { key: '__notes', label: 'Notes', multiline: true },
  { key: '__death', label: 'Death Info' },
  { key: '__lastVisit', label: 'Last Visit' },
  { key: '__updatedAt', label: 'Updated' },
];

export const MULTILINE_DETAIL_FIELDS = new Set(['__address', '__notes']);

export const normalizePatient = (patient, index = 0, fallbackId) => {
  const internalIdBase = getPatientId(patient) ?? fallbackId;
  const internalId = internalIdBase ? String(internalIdBase) : `patient-${index}`;
  const recordId = patient?.id
    ? String(patient.id)
    : patient?.ID_PT
    ? String(patient.ID_PT)
    : internalIdBase
    ? String(internalIdBase)
    : null;
  const firstName = patient?.firstName ?? patient?.first_name ?? '';
  const lastName = patient?.lastName ?? patient?.last_name ?? '';
  const baseDisplayName =
    `${lastName} ${firstName}`.replace(/\s+/g, ' ').trim() ||
    patient?.email ||
    patient?.EMAIL ||
    patient?.phone ||
    patient?.PHONE ||
    `Patient ${index + 1}`;
  const phoneRaw =
    patient?.phone ??
    patient?.mobile ??
    patient?.PHONE ??
    patient?.['ΤΗΛΕΦΩΝΟ'] ??
    patient?.['ΚΙΝΗΤΟ'] ??
    null;
  const emailRaw = patient?.email ?? patient?.EMAIL ?? null;
  const addressRaw = patient?.address ?? patient?.ADDRESS ?? patient?.['ΔΙΕΥΘΥΝΣΗ'] ?? null;
  const notesRaw = patient?.notes ?? patient?.NOTES ?? null;
  const diagnosisRaw = patient?.diagnosis ?? patient?.DIAGNOSIS ?? null;
  const deathRaw = patient?.death ?? patient?.DEATH ?? null;
  const amkaRaw = patient?.amka ?? patient?.AMKA ?? null;
  const amRaw = patient?.am ?? patient?.AM ?? null;
  const genderRaw = patient?.gender ?? null;
  const dateOfBirthRaw = patient?.dateOfBirth ?? patient?.date_birth ?? patient?.datebirth ?? patient?.date_of_birth ?? patient?.['ΗΜΕΡΟΜΗΝΙΑ ΓΕΝΝΗΣΗΣ'] ?? patient?.['HMEΡ_ΓΕΝΝΗΣΗΣ'] ?? null;
  const lastVisitRaw = patient?.last_visit ?? patient?.LAST_VISIT ?? patient?.lastVisit ?? null;
  const createdAtRaw = patient?.card_date ?? patient?.datekarta ?? patient?.created_at ?? patient?.CREATED_AT ?? patient?.createdAt ?? patient?.['ημερομηνια καρτελασ'] ?? patient?.['HMER_KARTAS'] ?? patient?.['ΗΜΕΡ_ΚΑΡΤΑΣ'] ?? patient?.['ΗΜΕΡ_ΚΑΡΤΑ'] ?? null;
  const updatedAtRaw = patient?.updated_at ?? patient?.UPDATED_AT ?? patient?.updatedAt ?? null;

  const ageValue =
    patient?.__age ?? patient?.age ?? patient?.AGE ?? computeAgeFromDate(dateOfBirthRaw);

  const phoneFormatted = formatFieldValue(phoneRaw);
  const emailFormatted = formatFieldValue(emailRaw);
  const addressFormatted = formatFieldValue(addressRaw);
  const notesFormatted = formatFieldValue(notesRaw);
  const diagnosisFormatted = formatFieldValue(diagnosisRaw);
  const deathFormatted = formatFieldValue(deathRaw);
  const amkaFormatted = formatFieldValue(amkaRaw);
  const amFormatted = formatFieldValue(amRaw);
  const genderFormatted = formatFieldValue(genderRaw);
  const ageFormatted = formatFieldValue(ageValue);
  const dateOfBirthFormatted = formatDateValue(dateOfBirthRaw);
  const lastVisitFormatted = formatDateValue(lastVisitRaw);
  const createdAtFormatted = formatDateValue(createdAtRaw);
  const updatedAtFormatted = formatDateValue(updatedAtRaw);

  const normalized = {
    ...patient,
    id: internalId,
    ID_PT: patient?.ID_PT ?? recordId ?? internalId,
    __internalId: internalId,
    __recordId: recordId,
    __originalId: patient?.id ?? patient?.ID_PT ?? null,
    __displayName: formatFieldValue(baseDisplayName),
    __phone: phoneFormatted,
    __email: emailFormatted,
    __address: addressFormatted,
    __notes: notesFormatted,
    __diagnosis: diagnosisFormatted,
    __death: deathFormatted,
    __amka: amkaFormatted,
    __am: amFormatted,
    __gender: genderFormatted,
    __age: ageFormatted,
    __dateOfBirth: dateOfBirthFormatted,
    __lastVisit: lastVisitFormatted,
    __createdAt: createdAtFormatted,
    __cardDate: createdAtFormatted,
    __updatedAt: updatedAtFormatted,
  };

  if (!normalized.first_name && firstName) {
    normalized.first_name = firstName;
  }
  if (!normalized.last_name && lastName) {
    normalized.last_name = lastName;
  }
  // Add camelCase versions for DataGrid compatibility
  if (!normalized.firstName && firstName) {
    normalized.firstName = firstName;
  }
  if (!normalized.lastName && lastName) {
    normalized.lastName = lastName;
  }
  if (!normalized.phone && phoneRaw) {
    normalized.phone = phoneRaw;
  }
  if (!normalized.email && emailRaw) {
    normalized.email = emailRaw;
  }
  if (!normalized.address && addressRaw) {
    normalized.address = addressRaw;
  }
  if (!normalized.notes && notesRaw) {
    normalized.notes = notesRaw;
  }
  if (!normalized.gender && genderRaw) {
    normalized.gender = genderRaw;
  }
  if (!normalized.date_of_birth && dateOfBirthRaw) {
    normalized.date_of_birth = dateOfBirthRaw;
  }
  if (!normalized.dateOfBirth && dateOfBirthRaw) {
    normalized.dateOfBirth = dateOfBirthRaw;
  }
  if (!normalized.amka && amkaRaw) {
    normalized.amka = amkaRaw;
  }
  if (!normalized.am && amRaw) {
    normalized.am = amRaw;
  }
  if (!normalized.diagnosis && diagnosisRaw) {
    normalized.diagnosis = diagnosisRaw;
  }
  if (!normalized.death && deathRaw) {
    normalized.death = deathRaw;
  }
  if (!normalized.last_visit && lastVisitRaw) {
    normalized.last_visit = lastVisitRaw;
  }
  if (!normalized.lastVisit && lastVisitRaw) {
    normalized.lastVisit = lastVisitRaw;
  }
  // Add camelCase for created_at/updated_at
  if (!normalized.created && createdAtRaw) {
    normalized.created = createdAtRaw;
  }
  if (!normalized.updated && updatedAtRaw) {
    normalized.updated = updatedAtRaw;
  }

  const detailEntries = PATIENT_DETAIL_FIELD_CONFIG.map(({ key, label, multiline }) => {
    const value = formatFieldValue(normalized[key]);

    return {
      key,
      label,
      value,
      multiline: Boolean(multiline) || MULTILINE_DETAIL_FIELDS.has(key),
    };
  }).filter((entry) => entry.value !== '—');

  normalized.__detailEntries = detailEntries;

  return normalized;
};
